/**
 * Google Maps browser key — must be a Maps JavaScript API key, NOT the Firebase web API key.
 */
export const FIREBASE_WEB_API_KEY = 'AIzaSyBmCiMaoZWOvvbV1OUjvIuwWiCgGbv_CWE';

export function isFirebaseWebApiKey(key) {
  const k = String(key || '').trim();
  if (!k) return false;
  if (k === FIREBASE_WEB_API_KEY) return true;
  const fromEnv = process.env.REACT_APP_FIREBASE_API_KEY;
  if (fromEnv && k === String(fromEnv).trim()) return true;
  return false;
}

const PLACEHOLDER_MAPS_KEY_PATTERNS = [
  /^YOUR_BROWSER_MAPS_API_KEY$/i,
  /^YOUR_MAPS_BROWSER_KEY$/i,
  /^YOUR_.*MAPS.*KEY$/i,
  /^your-maps-browser-key$/i,
  /^your-browser-key$/i,
  /^CHANGEME$/i,
  /^REPLACE_ME$/i,
  /^AIza\.\.\.$/i,
];

/** Example / template values from govops-config.json.example — not valid for Maps JS. */
export function isPlaceholderMapsKey(key) {
  const k = String(key || '').trim();
  if (!k) return true;
  if (PLACEHOLDER_MAPS_KEY_PATTERNS.some((re) => re.test(k))) return true;
  // Maps browser keys are ~39 chars; reject truncated doc examples.
  if (k.startsWith('AIza') && k.length < 35) return true;
  return false;
}

function readRawMapsKey() {
  // Build-time key wins over runtime window (avoids stale cached govops-config poisoning Maps).
  const fromEnv = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (fromEnv && String(fromEnv).trim()) return String(fromEnv).trim();
  if (typeof window !== 'undefined' && window.__GOOGLE_MAPS_API_KEY__) {
    return String(window.__GOOGLE_MAPS_API_KEY__).trim();
  }
  return '';
}

/** Valid Maps JS key for @react-google-maps/api (empty if missing or Firebase key). */
export function getGoogleMapsApiKey() {
  const raw = readRawMapsKey();
  if (!raw || isPlaceholderMapsKey(raw) || isFirebaseWebApiKey(raw)) return '';
  return raw;
}

/** `missing` | `placeholder` | `firebase_key` | null (ok) */
export function getGoogleMapsKeyIssue() {
  const raw = readRawMapsKey();
  if (!raw) return 'missing';
  if (isPlaceholderMapsKey(raw)) return 'placeholder';
  if (isFirebaseWebApiKey(raw)) return 'firebase_key';
  return null;
}

export function googleMapsSetupMessage(issue = getGoogleMapsKeyIssue()) {
  return getMapsUnavailableContent({ issue }).summary;
}

export function googleMapsLoadErrorMessage(loadError) {
  return getMapsUnavailableContent({ loadError }).summary;
}

/**
 * User-facing copy for MapsUnavailable (summary + optional admin hint).
 * @returns {{ title: string, summary: string, steps: string[], adminHint?: string, code: string }}
 */
export function getMapsUnavailableContent({
  issue = getGoogleMapsKeyIssue(),
  loadError = null,
} = {}) {
  const msg = loadError?.message || String(loadError || '');

  if (msg.includes('ApiNotActivatedMapError')) {
    return {
      code: 'api_not_activated',
      title: 'Interactive map is temporarily unavailable',
      summary:
        'Location maps and address search are not available right now. You can still submit permits with coordinates on the dashboard and view all permits in history.',
      steps: [
        'Use Dashboard to enter latitude and longitude manually.',
        'Open Permit history to review submitted permits.',
        'Contact support if you need help locating coordinates.',
      ],
      adminHint:
        'Enable Maps JavaScript API and Places API in Google Cloud Console for the RobinhoodSpace browser key, then set googleMapsApiKey in public/govops-config.json.',
    };
  }

  if (msg.includes('InvalidKeyMapError') || msg.includes('RefererNotAllowed')) {
    return {
      code: 'key_rejected',
      title: 'Interactive map is temporarily unavailable',
      summary:
        'The map service could not authorize this site. Permit submission and history are still available without the map.',
      steps: [
        'Submit permits from the Dashboard using coordinates.',
        'View permit status in Permit history.',
      ],
      adminHint:
        'Maps browser key HTTP referrers must include https://www.robinhoodspace.com/*',
    };
  }

  if (loadError && msg) {
    return {
      code: 'load_error',
      title: 'Interactive map is temporarily unavailable',
      summary:
        'We could not load the map right now. Your permits and GovOps data are unaffected.',
      steps: [
        'Try refreshing the page in a few minutes.',
        'Use Dashboard or Permit history while the map is offline.',
      ],
      adminHint: msg,
    };
  }

  if (issue === 'placeholder') {
    return {
      code: 'placeholder_key',
      title: 'Interactive map is not configured',
      summary:
        'The Maps browser key is still set to the example placeholder. Permit workflows work without the map.',
      steps: [
        'Submit permits on the Dashboard with latitude and longitude.',
        'Use Permit history to track status.',
        'Ask your administrator to set googleMapsApiKey in govops-config.json and redeploy.',
      ],
      adminHint:
        'Run: GOOGLE_MAPS_API_KEY=<browser-key> bash scripts/configure-google-maps.sh then rebuild and deploy hosting.',
    };
  }

  if (issue === 'firebase_key') {
    return {
      code: 'firebase_key',
      title: 'Interactive map is not configured',
      summary:
        'Map view and address search are not enabled for this environment yet. All permit workflows work without the map.',
      steps: [
        'Submit permits on the Dashboard with latitude and longitude.',
        'Use Permit history to track status and export records.',
      ],
      adminHint:
        'Use a dedicated Maps JavaScript API browser key (not the Firebase web key). Set googleMapsApiKey in public/govops-config.json.',
    };
  }

  return {
    code: issue === 'missing' ? 'missing_key' : 'unavailable',
    title: 'Interactive map is not configured',
    summary:
      'Maps are optional for RobinhoodSpace. You can submit and manage false-alarm permits using coordinates and permit history.',
    steps: [
      'Go to Dashboard → Submit new permit (latitude / longitude).',
      'Open Permit history for a full list and CSV export.',
      'Use Submit with address search once maps are enabled by your administrator.',
    ],
    adminHint:
      'Set googleMapsApiKey in public/govops-config.json and enable Maps JavaScript API + Places API on the key’s GCP project.',
  };
}
